import React from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument } from '@popety-io/types'
import { Icon } from '../../../../../atoms/Icon'

export interface DocumentsListTranslations {
  noDocuments?: string
  view?: string
}

const DEFAULT_TRANSLATIONS: DocumentsListTranslations = {
  noDocuments: 'No documents available',
  view: 'View',
}

interface DocumentsListProps {
  documents: LandUsePlanDocument[]
  onViewDocument: (doc: LandUsePlanDocument) => void
  translations?: DocumentsListTranslations
}

export const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  onViewDocument,
  translations,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }

  if (documents.length === 0) {
    return <EmptyState>{t.noDocuments}</EmptyState>
  }

  return (
    <ListContainer>
      {documents.map((doc) => (
        <DocumentItem key={doc.id}>
          <DocumentIcon>
            <Icon
              name={
                doc.plan_type_granularity === 'rule' ? 'file-text' : 'blueprint'
              }
              size="small"
            />
          </DocumentIcon>
          <DocumentTitle title={doc.title}>{doc.title}</DocumentTitle>
          <ViewButton
            onClick={() => onViewDocument(doc)}
            aria-label={`${t.view} ${doc.title}`}
          >
            {t.view} <Icon name="chevron-right" size="small" />
          </ViewButton>
        </DocumentItem>
      ))}
    </ListContainer>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

const DocumentIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const DocumentTitle = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ViewButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`
